import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

// ─── Layouts ──────────────────────────────────────────────────────────────────

const PublicLayout = () => import('@/layouts/PublicLayout.vue');
const AuthLayout = () => import('@/layouts/AuthLayout.vue');
const AppLayout = () => import('@/layouts/AppLayout.vue');

// ─── Views ────────────────────────────────────────────────────────────────────

const HomeView = () => import('@/views/HomeView.vue');
const LoginView = () => import('@/views/auth/LoginView.vue');
const RegisterView = () => import('@/views/auth/RegisterView.vue');
const DashboardView = () => import('@/views/app/DashboardView.vue');
const FoldersView = () => import('@/views/app/FoldersView.vue');
const FolderDetailView = () => import('@/views/app/FolderDetailView.vue');
const TtsDocumentView = () => import('@/views/app/TtsDocumentView.vue');
const HistoryView = () => import('@/views/app/HistoryView.vue');
const AccountProfileView = () => import('@/views/app/AccountProfileView.vue');
const AccountSubscriptionView = () => import('@/views/app/AccountSubscriptionView.vue');
const AccountBillingView = () => import('@/views/app/AccountBillingView.vue');
const NotFoundView = () => import('@/views/NotFoundView.vue');

// ─── Routes ───────────────────────────────────────────────────────────────────

const routes: RouteRecordRaw[] = [
  // Public
  {
    path: '/',
    component: PublicLayout,
    children: [{ path: '', name: 'home', component: HomeView }],
  },

  // Auth
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      { path: 'login', name: 'login', component: LoginView },
      { path: 'register', name: 'register', component: RegisterView },
    ],
  },

  // Authenticated app
  {
    path: '/app',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'dashboard' } },
      { path: 'dashboard', name: 'dashboard', component: DashboardView },
      { path: 'folders', name: 'folders', component: FoldersView },
      { path: 'folders/:folderId', name: 'folder-detail', component: FolderDetailView },
      { path: 'tts/:documentId', name: 'tts-document', component: TtsDocumentView },
      { path: 'history', name: 'history', component: HistoryView },
      { path: 'account/profile', name: 'account-profile', component: AccountProfileView },
      { path: 'account/subscription', name: 'account-subscription', component: AccountSubscriptionView },
      { path: 'account/billing', name: 'account-billing', component: AccountBillingView },
    ],
  },

  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView },
];

// ─── Router ───────────────────────────────────────────────────────────────────

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0 };
  },
});

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Try to restore from persisted token
    await authStore.restoreSession();
    if (!authStore.isAuthenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }
  }

  if ((to.name === 'login' || to.name === 'register') && authStore.isAuthenticated) {
    return next({ name: 'dashboard' });
  }

  next();
});

export default router;